import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Button, Paper, CircularProgress, Alert, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, fetchOptions } from '../../config/api';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  marginBottom: '2rem',
  textAlign: 'center',
});

const Message = styled(Typography)({
  fontSize: '1.2rem',
  color: '#666',
  textAlign: 'center',
  marginBottom: '2rem',
});

const StyledButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem 2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const IconWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '2rem',
  '& svg': {
    fontSize: '4rem',
    color: '#4caf50',
  },
});

const OrderConfirmation = styled(Paper)({
  padding: '2rem',
  marginBottom: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
  borderRadius: '4px',
});

const OrderDetails = styled(Box)({
  marginTop: '2rem',
  '& > *': {
    marginBottom: '1rem',
  },
});

const LineItem = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.5rem 0',
});

const Success = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { clearCart } = useCart();
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get('session_id');
    
    if (sessionId) {
      console.log('Session ID:', sessionId);
      const url = getApiUrl(`/api/checkout-session/${sessionId}`);
      console.log('Fetching session from:', url);

      fetch(url, {
        ...fetchOptions,
        method: 'GET',
      })
        .then(async res => {
          const text = await res.text();
          console.log('Raw response:', text);
          
          if (!res.ok) {
            try {
              const errorData = JSON.parse(text);
              throw new Error(errorData.error || 'Failed to fetch session details');
            } catch (e) {
              throw new Error(`Failed to fetch session details: ${text}`);
            }
          }
          
          return JSON.parse(text);
        })
        .then(data => {
          console.log('Session data:', data);
          setSession(data);
          setLoading(false);
          clearCart();
        })
        .catch(error => {
          console.error('Error fetching session:', error);
          setError(error.message);
          setLoading(false);
        });
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [location, clearCart]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <StyledButton component={Link} to="/" variant="contained">
              Return to Home
            </StyledButton>
          </Box>
        </Box>
      </Container>
    );
  }

  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <IconWrapper>
            <CheckCircleOutlineIcon />
          </IconWrapper>

          <PageTitle>Thank You!</PageTitle>
          
          <OrderConfirmation elevation={0}>
            <Typography variant="h6" gutterBottom align="center">
              Order Confirmation
            </Typography>
            <Message>
              Your order has been successfully placed. We'll send you an email confirmation
              with your order details and tracking information once your items have been shipped.
            </Message>

            {session && (
              <OrderDetails>
                <Typography variant="subtitle1">
                  Order Number: {session.payment_intent}
                </Typography>
                <Typography variant="subtitle1">
                  Date: {new Date(session.created * 1000).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
                {session.customer_email && (
                  <Typography variant="subtitle1">
                    Email: {session.customer_email}
                  </Typography>
                )}

                {session.shipping_details && (
                  <Box mt={2}>
                    <Typography variant="subtitle1" gutterBottom>
                      Shipping Address:
                    </Typography>
                    <Typography color="textSecondary">
                      {session.shipping_details.name}<br />
                      {session.shipping_details.address.line1}<br />
                      {session.shipping_details.address.line2 && (
                        <>{session.shipping_details.address.line2}<br /></>
                      )}
                      {session.shipping_details.address.city}, {session.shipping_details.address.state} {session.shipping_details.address.postal_code}<br />
                      {session.shipping_details.address.country}
                    </Typography>
                  </Box>
                )}

                {session.line_items && session.line_items.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      Order Items:
                    </Typography>
                    {session.line_items.map((item, index) => (
                      <LineItem key={index}>
                        <Box>
                          <Typography>
                            {item.description}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Quantity: {item.quantity}
                          </Typography>
                        </Box>
                        <Typography>
                          {formatPrice(item.amount_total, session.currency)}
                        </Typography>
                      </LineItem>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <LineItem>
                      <Typography variant="subtitle1">Total</Typography>
                      <Typography variant="subtitle1">
                        {formatPrice(session.amount_total, session.currency)}
                      </Typography>
                    </LineItem>
                  </Box>
                )}
              </OrderDetails>
            )}
          </OrderConfirmation>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {user ? (
              <StyledButton
                component={Link}
                to="/profile"
                variant="contained"
              >
                View Order History
              </StyledButton>
            ) : (
              <StyledButton
                component={Link}
                to="/login"
                variant="contained"
              >
                Sign In to View Orders
              </StyledButton>
            )}
            
            <StyledButton
              component={Link}
              to="/"
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                borderColor: '#1a1a1a',
                color: '#1a1a1a',
                '&:hover': {
                  backgroundColor: 'transparent',
                  borderColor: '#c4a661',
                  color: '#c4a661',
                },
              }}
            >
              Continue Shopping
            </StyledButton>
          </Box>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Success;
